import { createSlice } from '@reduxjs/toolkit'
import { diff } from 'deep-object-diff'
import API from '../../../axios'

const initialState = {
	data: [],
	dataNew: {},
	isModified: false,
	isFetchingData: true,
	currentTheme: 'dark',
	initialData: {},
	isSettingsModalOpen: false,
	isAcceptModalVisible: false,
	currentSection: 'licences',
	redirectedFrom: null,
	isReset: false,
	otherSettings: {
		countCell: null,
		isDetailMatrixInfo: true,
	},
	viewer: {
		mainMatrixValue: null,
	},
	modalSettings: {
		globalTabs: 'general',
		localTabs: 'presets',
	},
	currentPreset: 'default',
}

const slice = createSlice({
	name: 'preferences',
	initialState,
	reducers: {
		setRedirectedFrom(state, action) {
			state.redirectedFrom = action.payload
		},
		setCurrentPreset(state, action) {
			state.currentPreset = action.payload
		},
		setNewPresetList(state, action) {
			state.dataNew.presets = action.payload
		},
		setCurrentSection(state, action) {
			state.currentSection = action.payload
		},
		setAcceptModalVisibility(state, action) {
			state.isAcceptModalVisible = action.payload
		},
		toggleSettingsModal(state, action) {
			state.isSettingsModalOpen = action.payload
		},
		setPreferences(state, action) {
			state.data = action.payload
		},
		setFetching(state, action) {
			state.isFetchingData = action.payload
		},
		setPreferencesNew(state, action) {
			state.dataNew = action.payload
			state.initialData = action.payload
		},
		setCurrentTheme(state, action) {
			state.currentTheme = action.payload
		},
		setInitialState(state, action) {
			state.initialData = action.payload
		},
		setModified(state, action) {
			state.isModified = action.payload
		},
		resetChanges(state) {
			state.dataNew = state.initialData
			state.isAcceptModalVisible = false
			state.isModified = false
			state.currentSection = 'preferences'
		},
		setActions(state, action) {
			const { property, value } = action.payload
			state.dataNew[state.currentTheme].actions[property] = value
			state.isModified = true
		},
		setRaiseColors(state, action) {
			const { index, value } = action.payload
			state.dataNew[state.currentTheme].actions.raise[index] = value
			state.isModified = true
		},
		setSuitsColors(state, action) {
			const { property, value } = action.payload
			state.dataNew[state.currentTheme].cards.suits[property] = value
			state.isModified = true
		},
		setSubstrateColors(state, action) {
			const { property, value } = action.payload
			state.dataNew[state.currentTheme].matrix.substrate[property] = value
			state.isModified = true
		},
		setPositionsColors(state, action) {
			const { property, value } = action.payload
			state.dataNew[state.currentTheme].positions[property] = value
			state.isModified = true
		},
		setSubstrateSettings(state, action) {
			state.dataNew.common.matrix.useSubstrate = action.payload
			state.isModified = state.initialData.common.matrix.useSubstrate !== action.payload
			state.isModified = true
		},
		setRoundingSettings(state, action) {
			state.dataNew.common.matrix.rounding = action.payload
			state.isModified = state.initialData.common.matrix.rounding !== action.payload
			state.isModified = true
		},
		setIndentSettings(state, action) {
			state.dataNew.common.matrix.indent = action.payload
			state.isModified = state.initialData.common.matrix.indent !== action.payload
			state.isModified = true
		},
		setShadowSettings(state, action) {
			state.dataNew.common.matrix.shadow = action.payload
			state.dataNew[state.currentTheme].switcher.shadow = action.payload
			state.isModified = state.initialData.common.matrix.shadow !== action.payload
			state.isModified = true
		},
		setEquityLineSettings(state, action) {
			state.dataNew.common.matrix.equityLine = action.payload
			state.isModified = state.initialData.common.matrix.equityLine !== action.payload
			state.isModified = true
		},

		setIsShowTableSettings(state, action) {
			state.dataNew.common.matrix.table = action.payload
			state.isModified = state.initialData.common.matrix.table !== action.payload
			state.isModified = true
		},

		setIsShowTableFromViewer(state, action) {
			state.dataNew.common.matrix.table = action.payload
		},

		setEquityLineFromViewer(state, action) {
			state.dataNew.common.matrix.equityLine = action.payload
		},

		setIsShowQueryLineFromViewer(state, action) {
			state.dataNew.common.matrix.queryLine = action.payload
		},

		setIsShowPotPositionsLineFromViewer(state, action) {
			state.dataNew.common.matrix.potPositionsLine = action.payload
		},

		setIsShowPrizesLineFromViewer(state, action) {
			state.dataNew.common.matrix.prizesLine = action.payload
		},

		setIsShowOptimalDetailMatrixFromViewer(state, action) {
			state.dataNew.common.matrix.optimalDetailMatrix = action.payload
		},

		setIsShowTraceFromViewer(state, action) {
			state.dataNew.common.matrix.isShowTrace = action.payload
		},

		setSubstrateSize(state, action) {
			const { value, label } = action.payload
			const group = `fontSize${label}player`
			state.dataNew.common.matrix[group] = value
			state.isModified = state.initialData.common.matrix[group] === value
			state.isModified = true
		},

		setMatrixEntityColor(state, action) {
			const [title, label, value] = action.payload
			state.dataNew[state.currentTheme].matrix[title][label] = value
			state.isModified = true
		},

		setCardStyle(state, action) {
			state.dataNew.common.cards.cardLayout = action.payload
			state.isModified = true
		},

		// change state of checkbox
		setCheckboxState(state) {
			const copy = state.dataNew.common.cards.useCustomColors
			state.dataNew.common.cards.useCustomColors = !copy
			state.isModified = true
		},

		setHotkeys(state, action) {
			const { property, value, title } = action.payload
			state.dataNew[state.currentTheme].hotkeys[title][property] = value
		},
		resetHotkeys(state, action) {
			state.dataNew[state.currentTheme].hotkeys = action.payload
			state.isModified = true
		},

		setTrainer(state, action) {
			const { property, value } = action.payload
			state.dataNew[state.currentTheme].trainer[property] = value
			state.isModified = true
		},

		// renew initial data on save
		refreshInitialData(state) {
			state.initialData = state.dataNew
		},

		setThreshold(state, action) {
			state.dataNew.common.matrix.threshold = action.payload
		},

		setAbsThreshold(state, action) {
			state.dataNew.common.matrix.threshold = action.payload
			state.isModified = true
		},
		setViewerSizings(state, action) {
			state.dataNew.common.matrix.viewerSizings = action.payload
		},
		setStackCardCurrentStatus(state, action) {
			state.dataNew.common.isStackCardShow = action.payload
		},
		setActionsOrder(state, action) {
			state.dataNew.common.actionsOrder = action.payload
			state.isModified = true
		},
		setReset(state, action) {
			state.isReset = action.payload
		},
		setDrawReverseRaiseBetActions(state, action) {
			state.dataNew.common.isDrawReverseActions = action.payload
			state.isModified = true
		},
		setHiddenTooltips(state, action) {
			state.dataNew.common.viewer.matrixTooltip = action.payload.matrixTooltip
			state.dataNew.common.viewer.combosTooltip = action.payload.combosTooltip
			state.dataNew.common.viewer.detailMatrixTooltip = action.payload.detailMatrixTooltip
		},
		setFlopMapMode(state, action) {
			state.dataNew.common.isFlopMapWithEmptyLines = action.payload
		},
		setStrategyTooltipMode(state, action) {
			state.dataNew.common.isShowDetailedTooltip = action.payload
		},
		setStrategySortViewMode(state, action) {
			state.dataNew.common.isShowFullStrategyFlop = action.payload
		},
		setOtherSettings(state, action) {
			state.otherSettings = {
				...state.otherSettings,
				...action.payload,
			}
		},
		setIsDetailMatrixInfo(state, action) {
			state.otherSettings.isDetailMatrixInfo = !state.otherSettings.isDetailMatrixInfo
		},
		setMainMatrixValue(state, action) {
			state.viewer.mainMatrixValue = action.payload
		},
		setModalGlobalTab(state, action) {
			state.modalSettings.globalTabs = action.payload.globalTab
			state.modalSettings.localTabs = action.payload.localTab
		},
		setModalLocalTab(state, action) {
			state.modalSettings.localTabs = action.payload
		},
		setTextShadow(state, action) {
			const { mode, value } = action.payload
			state.dataNew[state.currentTheme].matrix.textShadow[mode] = value
			state.isModified = true
		},
		setTextSize(state, action) {
			const { mode, value } = action.payload
			state.dataNew[state.currentTheme].matrix.textSize[mode] = value
			state.isModified = true
		},
		setAppliedPreset(state, action) {
			state.dataNew.common.matrix.indent = false
			state.dataNew.common.matrix.rounding = false
			state.dataNew.common.matrix.useSubstrate = false
			state.dataNew[state.currentTheme] = action.payload
			state.isModified = true
		},
		setAppliedTextSettingsFromPreview(state, action) {
			const { textColor, textSize, textShadow } = action.payload

			state.dataNew[state.currentTheme].matrix.substrate.textcolor1playermatrix = textColor
			state.dataNew.common.matrix.shadow = textShadow
			state.dataNew.common.matrix.fontSize1player = textSize
		},
		setPresetProperty(state, action) {
			const { preset, type, property, isSuits } = action.payload

			if (!type && !isSuits) {
				state.dataNew[state.currentTheme][property] = preset[property]
			}

			if (type && !isSuits) {
				state.dataNew[state.currentTheme][type][property] = preset[type][property]
			}

			if (isSuits) {
				state.dataNew[state.currentTheme].cards.suits = preset.cards.suits
				state.dataNew[state.currentTheme].trainer.table = preset.trainer.table
			}

			state.isModified = true
		},
		setCommonMatrixProperty(state, action) {
			const { type, value } = action.payload
			state.dataNew.common.matrix[type] = value
			state.isModified = true
		},
		setIsShowAdvancedSettings(state, action) {
			const { type, value } = action.payload
			state.dataNew.common.isShowAdvancedSettings[type] = value
			state.isModified = true
		},
	},
})

export const { actions, reducer } = slice

/**
 * Function that updates preferences state depending on type
 * @param property - changed property name
 * @param type  - type of changed preference
 * @param value - new value of changed property
 * @param title - section label of nested property
 * */
export const setNewPreferences = (property, value, type, title) => (dispatch) => {
	switch (type) {
		case 'actions':
			dispatch(actions.setActions({ property, value }))
			break
		case 'matrix':
		case '':
			dispatch(actions.setMatrixEntityColor([title, property, value]))
			break
		case 'suits':
			dispatch(actions.setSuitsColors({ property, value }))
			break
		case 'positions':
			dispatch(actions.setPositionsColors({ property, value }))
			break
		case 'hotkeys':
			dispatch(actions.setHotkeys({ property, value, title }))
			break
		case 'trainer':
			dispatch(actions.setTrainer({ property, value }))
			break
	}
}

export const setApplyModified = (isApply) => (dispatch) => dispatch(actions.setModified(isApply))
export const setRedirectedPoint = (path) => (dispatch) => dispatch(actions.setRedirectedFrom(path))
export const setCurrentSection = (section) => (dispatch) => dispatch(actions.setCurrentSection(section))
export const toggleCheckboxState = () => (dispatch) => dispatch(actions.setCheckboxState())

/**
 * Function that sets whether matrix cell use substrate or not
 * @param value {boolean}
 * */
export const setSubstrateUsage = (value) => (dispatch) => dispatch(actions.setSubstrateSettings(value))
export const setRoundingUsage = (value) => (dispatch) => dispatch(actions.setRoundingSettings(value))
export const setIndentUsage = (value) => (dispatch) => dispatch(actions.setIndentSettings(value))
export const setShadowUsage = (value) => (dispatch) => dispatch(actions.setShadowSettings(value))
// export const setEquityLine = (value) => (dispatch) => dispatch(actions.setEquityLineSettings(value))
// export const setIsShowTable = (value) => (dispatch) => dispatch(actions.setIsShowTableSettings(value))
export const setAbsThreshold = (value) => (dispatch) => dispatch(actions.setAbsThreshold(value))
export const setViewerSizings = (value) => (dispatch) => dispatch(actions.setViewerSizings(value))
export const setActionsOrder = (value) => (dispatch) => dispatch(actions.setActionsOrder(value))

export const setCardStyle = (value) => (dispatch) => dispatch(actions.setCardStyle(value))

export const toggleSettingsDrawer = (payload) => (dispatch, getState) => {
	const { isModified } = getState().preferences

	if (isModified) {
		dispatch(actions.setAcceptModalVisibility(true))
	}

	dispatch(actions.toggleSettingsModal(payload))
}

export const setRaisesColors = (value, index) => (dispatch) => dispatch(actions.setRaiseColors({ value, index }))

export const revertChangedSettings = () => (dispatch) => dispatch(actions.resetChanges())

/**
 * Function that toggles theme from white to black and black to white etc...
 * */
export const toggleTheme = () => (dispatch, getState) => {
	const { theme } = getState().common

	if (theme === 'dark') {
		dispatch(actions.setCurrentTheme('light'))
	} else {
		dispatch(actions.setCurrentTheme('dark'))
	}

	dispatch(actions.setModified(true))
}

function sortSettings(setting, type) {
	const settingArray = Object.keys(setting).sort().reverse()

	if (type === 'ev') {
		const sortOrder = ['negEV', 'zeroEV', 'posEV']

		return sortOrder.reduce((acc, current) => {
			acc[current] = setting[current]

			return acc
		}, {})
	}

	return settingArray.reduce((acc, current) => {
		acc[current] = setting[current]

		return acc
	}, {})
}

export const fetchPreferences = () => async (dispatch) => {
	try {
		dispatch(actions.setFetching(true))
		const response = await API.get('get_settings')

		const { ev, equity, range } = response.data.dark.matrix

		const sortedEV = sortSettings(ev, 'ev')
		const sortedEquity = sortSettings(equity)
		const sortedRange = sortSettings(range)

		response.data.dark.matrix.ev = sortedEV
		response.data.dark.matrix.equity = sortedEquity
		response.data.dark.matrix.range = sortedRange

		response.data.presets = response.data.presets.map((preset) => {
			const { ev, equity, range } = preset.value.matrix

			const sortedEV = sortSettings(ev, 'ev')
			const sortedEquity = sortSettings(equity)
			const sortedRange = sortSettings(range)

			preset.value.matrix.ev = sortedEV
			preset.value.matrix.equity = sortedEquity
			preset.value.matrix.range = sortedRange

			return preset
		})

		dispatch(actions.setPreferencesNew(response.data))
		dispatch(actions.setCurrentTheme(response.data.common.theme))
		dispatch(actions.setFetching(false))
	} catch (e) {
		console.log(e)
		dispatch(actions.setFetching(false))
		console.warn('Something went wrong while fetching preferences data...')
	}
}

export const setPreferences = () => async (dispatch, getState) => {
	try {
		const { dataNew, initialData, currentTheme } = getState().preferences
		dispatch(actions.setFetching(true))
		dispatch(actions.refreshInitialData())
		dispatch(actions.setCurrentSection('preferences'))

		const data = diff(initialData, dataNew)

		if (data[currentTheme]?.actions?.raise) {
			data[currentTheme].actions.raise = dataNew[currentTheme].actions.raise
		}

		if (data?.common?.actionsOrder) {
			data.common.actionsOrder = dataNew.common.actionsOrder
		}

		if (Object.keys(data).length !== 0) {
			await API.post('set_settings', { colors: data })
		}

		dispatch(actions.setFetching(false))
		dispatch(actions.setModified(false))

		dispatch(actions.setAcceptModalVisibility(false))
	} catch (e) {
		dispatch(actions.setFetching(false))
		console.warn('Something went wrong while setting preferences data...', e)
	}
}

/**
 *
 *
 * **/
export const setIsShowTableFromViewer = (value) => (dispatch) => {
	dispatch(actions.setIsShowTableFromViewer(value))
	dispatch(setPreferences())
}

export const setIsShowQueryLineFromViewer = (value) => (dispatch) => {
	dispatch(actions.setIsShowQueryLineFromViewer(value))
	dispatch(setPreferences())
}

export const setIsShowPotPositionsLineFromViewer = (value) => (dispatch) => {
	dispatch(actions.setIsShowPotPositionsLineFromViewer(value))
	dispatch(setPreferences())
}

export const setIsShowPrizesLineFromViewer = (value) => (dispatch) => {
	dispatch(actions.setIsShowPrizesLineFromViewer(value))
	dispatch(setPreferences())
}

export const setIsShowOptimalDetailMatrixFromViewer = (value) => (dispatch) => {
	dispatch(actions.setIsShowOptimalDetailMatrixFromViewer(value))
	dispatch(setPreferences())
}

export const setControlViewerTooltip = (payload) => (dispatch) => {
	dispatch(actions.setHiddenTooltips(payload))
	dispatch(setPreferences())
}

export const setFlopMapMode = (mode) => (dispatch) => {
	dispatch(actions.setFlopMapMode(mode))
	dispatch(setPreferences())
}

export const setStrategyTooltipMode = (mode) => (dispatch) => {
	dispatch(actions.setStrategyTooltipMode(mode))
	dispatch(setPreferences())
}

export const setStrategySortViewMode = (mode) => (dispatch) => {
	dispatch(actions.setStrategySortViewMode(mode))
	dispatch(setPreferences())
}

export const toggleSizings = (payload) => (dispatch) => {
	dispatch(actions.setViewerSizings(payload))
	dispatch(setPreferences())
}

export const toggleStackCardViewStatus = (payload) => (dispatch) => {
	dispatch(actions.setStackCardCurrentStatus(payload))
	dispatch(setPreferences())
}

export const setEquityLineFromViewer = (value) => (dispatch) => {
	dispatch(actions.setEquityLineFromViewer(value))
	dispatch(setPreferences())
}

export const setThresholdFromViewer = (value) => (dispatch) => {
	dispatch(actions.setThreshold(value))
	dispatch(setPreferences())
}

export const setIsShowTraceFromViewer = (value) => (dispatch) => {
	dispatch(actions.setIsShowTraceFromViewer(value))
	dispatch(setPreferences())
}

export const setReset = (isReset) => (dispatch) => {
	dispatch(actions.setReset(isReset))
	dispatch(actions.setModified(false))
}

export const setOtherSettings = (settings) => (dispatch) => {
	dispatch(actions.setOtherSettings(settings))
}

export const setIsDetailMatrixInfo = () => (dispatch) => {
	dispatch(actions.setIsDetailMatrixInfo())
}

export const setMainMatrixValue = (value) => (dispatch) => {
	dispatch(actions.setMainMatrixValue(value))
}

/**
 * Function that makes API request and set all user preferences to default
 * */
export const resetToDefaultColors = () => async (dispatch) => {
	try {
		dispatch(actions.setFetching(true))
		await API.get('reset_settings')
		dispatch(actions.setModified(false))
		dispatch(fetchPreferences())
		dispatch(actions.setReset(true))
	} catch (e) {
		console.warn('Failed to get default colors: ', e)
	}
}
